/**
 * visitors.ts - Visitor type definitions
 *
 * Roller-coaster-tycoon style: each visitor type has unique psychology.
 * fearTolerance determines the "sweet spot" for satisfaction.
 */

export type VisitorTypeId =
  | 'scaredy_kid'
  | 'normal'
  | 'brave'
  | 'couple'
  | 'youtuber'
  | 'exorcist'
  | 'thrill_seeker'
  | 'coward';

export interface VisitorStatPreference {
  fear: number;       // 0-2: weight for fear stat (0=dislikes, 1=neutral, 2=loves)
  thrill: number;
  atmosphere: number;
  fun: number;
}

export interface VisitorTypeDef {
  id: VisitorTypeId;
  nameKey: string;
  resistanceMultiplier: number; // 1.0 = normal resistance
  rewardMultiplier: number;     // SP reward multiplier
  initialFear: number;          // 0-100
  // === Roller-coaster-tycoon style attributes ===
  fearTolerance: number;        // 0-100: sweet spot center (80-120% of this = optimal)
  baseExcitement: number;       // starting excitement
  nauseaRate: number;           // how fast nausea builds when over-scared (0-1)
  baseBudget: number;           // SP they might spend at gift shop etc.
  basePatience: number;         // patience (decreases on empty rooms)
  reviewInfluence: number;      // fame multiplier from reviews (1.0 = normal)
  fleeThreshold: number;       // satisfaction below this = flee (0-1)
  statPreference: VisitorStatPreference; // how much each emotion stat matters to this type
  descKey: string;              // i18n description key
}

export const VISITOR_TYPES: Record<VisitorTypeId, VisitorTypeDef> = {
  scaredy_kid: {
    id: 'scaredy_kid',
    nameKey: 'visitor_scaredy_kid',
    resistanceMultiplier: 0.5,
    rewardMultiplier: 0.5,
    initialFear: 20,
    fearTolerance: 25,
    baseExcitement: 30,
    nauseaRate: 0.8,
    baseBudget: 5,
    basePatience: 60,
    reviewInfluence: 0.5,
    fleeThreshold: 0.15,
    statPreference: { fear: 0.5, thrill: 0.5, atmosphere: 1.0, fun: 2.0 },
    descKey: 'visitor_scaredy_kid_desc',
  },
  normal: {
    id: 'normal',
    nameKey: 'visitor_normal',
    resistanceMultiplier: 1.0,
    rewardMultiplier: 1.0,
    initialFear: 0,
    fearTolerance: 50,
    baseExcitement: 20,
    nauseaRate: 0.4,
    baseBudget: 15,
    basePatience: 80,
    reviewInfluence: 1.0,
    fleeThreshold: 0.1,
    statPreference: { fear: 1.0, thrill: 1.0, atmosphere: 1.0, fun: 1.0 },
    descKey: 'visitor_normal_desc',
  },
  brave: {
    id: 'brave',
    nameKey: 'visitor_brave',
    resistanceMultiplier: 2.0,
    rewardMultiplier: 2.0,
    initialFear: 0,
    fearTolerance: 80,
    baseExcitement: 10,
    nauseaRate: 0.2,
    baseBudget: 25,
    basePatience: 90,
    reviewInfluence: 1.0,
    fleeThreshold: 0.05,
    statPreference: { fear: 1.5, thrill: 1.5, atmosphere: 1.0, fun: 0.8 },
    descKey: 'visitor_brave_desc',
  },
  couple: {
    id: 'couple',
    nameKey: 'visitor_couple',
    resistanceMultiplier: 0.8,
    rewardMultiplier: 1.5,
    initialFear: 10,
    fearTolerance: 40,
    baseExcitement: 40,
    nauseaRate: 0.5,
    baseBudget: 30,
    basePatience: 70,
    reviewInfluence: 1.2,
    fleeThreshold: 0.12,
    statPreference: { fear: 0.8, thrill: 1.5, atmosphere: 2.0, fun: 1.5 },
    descKey: 'visitor_couple_desc',
  },
  youtuber: {
    id: 'youtuber',
    nameKey: 'visitor_youtuber',
    resistanceMultiplier: 1.2,
    rewardMultiplier: 1.0,
    initialFear: 0,
    fearTolerance: 55,
    baseExcitement: 50,
    nauseaRate: 0.3,
    baseBudget: 20,
    basePatience: 95,
    reviewInfluence: 2.5,
    fleeThreshold: 0.08,
    statPreference: { fear: 1.5, thrill: 2.0, atmosphere: 1.0, fun: 1.5 },
    descKey: 'visitor_youtuber_desc',
  },
  exorcist: {
    id: 'exorcist',
    nameKey: 'visitor_exorcist',
    resistanceMultiplier: 3.0,
    rewardMultiplier: 3.0,
    initialFear: 0,
    fearTolerance: 95,
    baseExcitement: 5,
    nauseaRate: 0.1,
    baseBudget: 50,
    basePatience: 100,
    reviewInfluence: 1.5,
    fleeThreshold: 0.02,
    statPreference: { fear: 0.3, thrill: 0.3, atmosphere: 2.0, fun: 0.5 },
    descKey: 'visitor_exorcist_desc',
  },
  thrill_seeker: {
    id: 'thrill_seeker',
    nameKey: 'visitor_thrill_seeker',
    resistanceMultiplier: 1.5,
    rewardMultiplier: 1.8,
    initialFear: 0,
    fearTolerance: 70,
    baseExcitement: 60,
    nauseaRate: 0.15,
    baseBudget: 35,
    basePatience: 50,
    reviewInfluence: 1.3,
    fleeThreshold: 0.05,
    statPreference: { fear: 2.0, thrill: 2.0, atmosphere: 0.5, fun: 0.5 },
    descKey: 'visitor_thrill_seeker_desc',
  },
  coward: {
    id: 'coward',
    nameKey: 'visitor_coward',
    resistanceMultiplier: 0.3,
    rewardMultiplier: 0.8,
    initialFear: 40,
    fearTolerance: 15,
    baseExcitement: 50,
    nauseaRate: 0.9,
    baseBudget: 10,
    basePatience: 40,
    reviewInfluence: 0.8,
    fleeThreshold: 0.2,
    statPreference: { fear: 0.3, thrill: 0.5, atmosphere: 1.5, fun: 2.0 },
    descKey: 'visitor_coward_desc',
  },
};

export const ALL_VISITOR_TYPE_IDS = Object.keys(VISITOR_TYPES) as VisitorTypeId[];

/**
 * Bell curve satisfaction: peaks at ratio=1.0 (fear matches tolerance).
 * Returns 0..1 where 1 = perfect satisfaction.
 *
 * - ratio < 0.5: very bored (satisfaction ~0.1)
 * - ratio 0.8-1.2: sweet spot (satisfaction 0.8-1.0)
 * - ratio > 1.5: too much fear (satisfaction drops, nausea rises)
 * - ratio > 2.0: panic/flee territory
 */
export function calcSatisfactionCurve(totalFear: number, fearTolerance: number): number {
  if (fearTolerance <= 0) return totalFear === 0 ? 1.0 : 0.0;
  const ratio = totalFear / fearTolerance;

  // Gaussian bell curve centered at 1.0 with sigma=0.4
  const sigma = 0.4;
  const exponent = -Math.pow(ratio - 1.0, 2) / (2 * sigma * sigma);
  return Math.max(0, Math.min(1, Math.exp(exponent)));
}

/**
 * Calculate nausea from over-scaring.
 * Only builds when fear exceeds 120% of tolerance.
 */
export function calcNausea(totalFear: number, fearTolerance: number, nauseaRate: number): number {
  if (fearTolerance <= 0) return 0;
  const ratio = totalFear / fearTolerance;
  if (ratio <= 1.2) return 0;
  // Nausea builds quadratically above the threshold
  const excess = ratio - 1.2;
  return Math.min(100, excess * excess * 100 * nauseaRate);
}

/**
 * Determine if visitor should flee (too scared / too bored).
 * Returns true if the visitor wants to leave early.
 */
export function shouldVisitorFlee(
  satisfaction: number,
  nausea: number,
  patience: number,
  fleeThreshold: number,
): boolean {
  // Flee if nausea is extreme
  if (nausea >= 80) return true;
  // Flee if satisfaction is critically low and patience is gone
  if (satisfaction < fleeThreshold && patience <= 10) return true;
  return false;
}
